/** Cover media helpers: renderer (with graceful fallback) + inline uploader. */
import { useState } from 'react';
import { ImageIcon, Loader2, Upload, X } from 'lucide-react';
import { uploadMedia, mediaUrl } from '@/services/api/media';
import { formatApiError } from '@/services/api/client';
import { toast } from '@/components/ui/sonner';

/** Renders a cover image from a signed (relative) url, with a themed placeholder. */
export function CoverImage({ url, alt = '', className = '', icon: Icon = ImageIcon }) {
	const [failed, setFailed] = useState(false);
	const src = url ? mediaUrl(url) : null;
	if (!src || failed) {
		return (
			<div className={`flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 ${className}`}>
				<Icon className="text-slate-600" size={30} />
			</div>
		);
	}
	return (
		<img
			src={src}
			alt={alt}
			loading="lazy"
			onError={() => setFailed(true)}
			className={`object-cover ${className}`}
		/>
	);
}

/** Inline cover uploader used inside admin forms. Emits the media id on change. */
export function CoverUpload({ previewUrl, onChange, testId = 'cover-upload-input' }) {
	const [busy, setBusy] = useState(false);
	const [preview, setPreview] = useState(previewUrl || null);

	const onFile = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setBusy(true);
		try {
			const m = await uploadMedia(file);
			onChange(m.id);
			setPreview(mediaUrl(m.url));
			toast.success('Immagine di copertina caricata');
		} catch (err) {
			toast.error(formatApiError(err.response?.data?.detail));
		} finally {
			setBusy(false);
			e.target.value = '';
		}
	};

	const clear = () => { setPreview(null); onChange(null); };

	return (
		<div>
			{preview ? (
				<div className="relative overflow-hidden rounded-xl border border-slate-700">
					<img src={preview} alt="copertina" className="h-40 w-full object-cover" />
					<button
						type="button"
						onClick={clear}
						data-testid="cover-remove-button"
						className="absolute right-2 top-2 rounded-md bg-slate-950/80 p-1.5 text-rose-300 hover:bg-slate-950"
					>
						<X size={14} />
					</button>
				</div>
			) : (
				<label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-600 py-6 text-sm text-slate-300 hover:border-emerald-400/40">
					{busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} className="text-emerald-300" />}
					<span>{busy ? 'Caricamento…' : 'Carica immagine di copertina'}</span>
					<input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} data-testid={testId} />
				</label>
			)}
		</div>
	);
}
